import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import {
  getBibliothequeItems,
  getBibliothequeCategories,
} from "../../services/api";
import { BibliothequeItem, BibliothequeCategorie } from "../../types/types";
import { getFileName, resolveMediaUrl } from "../../utils/media";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faSearch,
  faFilter,
  faSpinner,
  faEye,
  faDownload,
  faTimes,
  faExpand,
  faCompress,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const PhotosPage: React.FC = () => {
  const { language } = useLanguage();
  const [photos, setPhotos] = useState<BibliothequeItem[]>([]);
  const [categories, setCategories] = useState<BibliothequeCategorie[]>([]);
  const [filteredPhotos, setFilteredPhotos] = useState<BibliothequeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [expandedImage, setExpandedImage] = useState<number | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<BibliothequeItem | null>(
    null
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Find photos category ID
  const photosCategoryId = categories.find(
    (cat) => cat.nomarabe === "صور" || cat.nomfrancais === "PHOTOS"
  )?.id;

  useEffect(() => {
    fetchPhotos();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (photosCategoryId) {
      filterPhotos();
    }
  }, [photos, searchTerm, selectedCategory, photosCategoryId]);

  // Reset current image index when selected photo changes
  useEffect(() => {
    if (selectedPhoto) {
      setCurrentImageIndex(0);
    }
  }, [selectedPhoto]);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const response = await getBibliothequeItems();
      const allItems = response.data || [];

      // Filter only active items
      const activeItems = allItems.filter(
        (item: BibliothequeItem) => item.etat
      );
      setPhotos(activeItems);
      setFilteredPhotos(activeItems);
    } catch (error) {
      console.error("Error fetching photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getBibliothequeCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const filterPhotos = () => {
    let filtered = photos;

    // Filter by photos category
    if (photosCategoryId) {
      filtered = filtered.filter(
        (item) => item.categorieId === photosCategoryId
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.descriptionarabe
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.descriptionfrancais
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Filter by selected category (if any additional filtering needed)
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (item) => item.categorieId === parseInt(selectedCategory)
      );
    }

    setFilteredPhotos(filtered);
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find((c) => c.id === categoryId);
    return category
      ? language === "ar"
        ? category.nomarabe
        : category.nomfrancais
      : "";
  };

  const openPhotoModal = (photo: BibliothequeItem) => {
    setSelectedPhoto(photo);
    setCurrentImageIndex(0);
    document.body.style.overflow = "hidden";
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = "unset";
  };

  const nextImage = () => {
    if (selectedPhoto && selectedPhoto.fichiers) {
      const nextIndex = (currentImageIndex + 1) % selectedPhoto.fichiers.length;
      setCurrentImageIndex(nextIndex);
    }
  };

  const prevImage = () => {
    if (selectedPhoto && selectedPhoto.fichiers) {
      const prevIndex =
        currentImageIndex === 0
          ? selectedPhoto.fichiers.length - 1
          : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
    }
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedPhoto) return;

      if (e.key === "ArrowRight") {
        nextImage();
      } else if (e.key === "ArrowLeft") {
        prevImage();
      } else if (e.key === "Escape") {
        closePhotoModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedPhoto, currentImageIndex]);

  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }

    // Reset touch positions
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Handle download
  const downloadImage = () => {
    if (!selectedPhoto?.fichiers?.[currentImageIndex]) return;

    const link = document.createElement("a");
    const currentFile = selectedPhoto.fichiers[currentImageIndex];
    link.href = resolveMediaUrl(currentFile);
    link.download = getFileName(currentFile) || `photo-${selectedPhoto.id}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get image URL helper
  const getImageUrl = (fileName: string) => resolveMediaUrl(fileName);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-mauritania-green-dark text-4xl animate-spin mb-4"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-2/3 mb-8 md:mb-0">
              <h1 className="text-4xl font-bold mb-4">
                {language === "ar" ? "معرض الصور" : "Galerie Photos"}
              </h1>
              <p className="text-xl opacity-90">
                {language === "ar"
                  ? "مجموعة متنوعة من الصور واللقطات التوثيقية"
                  : "Collection diversifiée de photos et de clichés documentaires"}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white p-3 rounded-full">
                <FontAwesomeIcon
                  icon={faImage}
                  className="text-green-600 text-3xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pr-10 pl-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={
                  language === "ar"
                    ? "ابحث في الصور..."
                    : "Rechercher des photos..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Photos Gallery */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {filteredPhotos.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow">
              <FontAwesomeIcon
                icon={faImage}
                className="text-gray-400 text-6xl mb-4"
              />
              <h3 className="text-2xl font-bold text-gray-600 mb-2">
                {language === "ar"
                  ? "لا توجد صور متاحة"
                  : "Aucune photo disponible"}
              </h3>
              <p className="text-gray-500">
                {language === "ar"
                  ? "لم يتم إضافة أي صور بعد"
                  : "Aucune photo n'a été ajoutée pour le moment"}
              </p>
            </div>
          ) : (
            <>
              {/* Results Info */}
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {language === "ar"
                      ? "مجموعة الصور"
                      : "Collection de Photos"}
                  </h2>
                  <p className="text-gray-600">
                    {language === "ar"
                      ? `عرض ${filteredPhotos.length} من ${photos.length} صورة`
                      : `Affichage de ${filteredPhotos.length} sur ${photos.length} photos`}
                  </p>
                </div>
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                  {filteredPhotos.length}{" "}
                  {language === "ar" ? "صورة" : "photos"}
                </div>
              </div>

              {/* Photos Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                    onClick={() => openPhotoModal(photo)}
                  >
                    {/* Photo Preview */}
                    <div className="relative h-64 overflow-hidden">
                      {photo.fichiers && photo.fichiers.length > 0 && (
                        <>
                          <img
                            src={getImageUrl(photo.fichiers[0])}
                            alt={
                              language === "ar"
                                ? photo.descriptionarabe
                                : photo.descriptionfrancais
                            }
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                            <FontAwesomeIcon
                              icon={faExpand}
                              className="text-white text-3xl opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                            />
                          </div>
                          <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                            {photo.fichiers.length}{" "}
                            {language === "ar" ? "صورة" : "photos"}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Photo Info */}
                    <div className="p-4">
                      <h3 className="font-bold text-gray-800 mb-2 line-clamp-2">
                        {language === "ar"
                          ? photo.descriptionarabe
                          : photo.descriptionfrancais}
                      </h3>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span className="flex items-center">
                          <FontAwesomeIcon icon={faImage} className="ml-1" />
                          {getCategoryName(photo.categorieId)}
                        </span>
                        <span className="text-green-600 font-medium">
                          #{photo.ordre}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Photo Modal with Carousel */}
      {selectedPhoto && selectedPhoto.fichiers && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={closePhotoModal}
        >
          <div className="relative w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              className="absolute top-4 left-4 z-20 text-white bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-all"
              onClick={closePhotoModal}
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            {/* Navigation Arrows */}
            {selectedPhoto.fichiers.length > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden md:block"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                </button>
                <button
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 text-white bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70 transition-all hidden md:block"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                >
                  <FontAwesomeIcon icon={faChevronRight} size="lg" />
                </button>
              </>
            )}

            {/* Carousel Container */}
            <div
              className="bg-black rounded-lg overflow-hidden w-full h-full"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Main Image Display */}
              <div className="relative w-full h-[70vh] flex items-center justify-center">
                <img
                  src={getImageUrl(selectedPhoto.fichiers[currentImageIndex])}
                  alt={`${
                    language === "ar"
                      ? selectedPhoto.descriptionarabe
                      : selectedPhoto.descriptionfrancais
                  } - Image ${currentImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />

                {/* Image Counter */}
                <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                  {currentImageIndex + 1} / {selectedPhoto.fichiers.length}
                </div>

                {/* Mobile Navigation Buttons */}
                {selectedPhoto.fichiers.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 md:hidden">
                    <button
                      className="bg-black bg-opacity-50 text-white rounded-full p-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className="bg-black bg-opacity-50 text-white rounded-full p-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}

                {/* Keyboard Navigation Hint */}
                <div className="absolute bottom-4 right-4 text-white text-xs opacity-70 hidden md:block">
                  {language === "ar"
                    ? "استخدم الأسهم للتنقل"
                    : "Use arrows to navigate"}
                </div>
              </div>

              {/* Thumbnail Strip */}
              {selectedPhoto.fichiers.length > 1 && (
                <div className="bg-gray-900 p-4">
                  <div className="flex overflow-x-auto space-x-2 py-2">
                    {selectedPhoto.fichiers.map((image, index) => (
                      <button
                        key={index}
                        className={`flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 transition-all ${
                          index === currentImageIndex
                            ? "border-green-500"
                            : "border-transparent hover:border-white"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToImage(index);
                        }}
                      >
                        <img
                          src={getImageUrl(image)}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Details */}
              <div className="bg-white p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {language === "ar"
                        ? selectedPhoto.descriptionarabe
                        : selectedPhoto.descriptionfrancais}
                    </h2>
                    <div className="flex flex-wrap gap-4 mt-2">
                      <div className="flex items-center text-gray-600">
                        <FontAwesomeIcon icon={faImage} className="ml-2" />
                        <span>
                          {getCategoryName(selectedPhoto.categorieId)}
                        </span>
                      </div>
                      <div className="text-gray-600">
                        {language === "ar" ? "رقم الترتيب:" : "Numéro d'ordre:"}{" "}
                        <span className="font-bold">{selectedPhoto.ordre}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-3 mt-4 md:mt-0">
                    <button
                      onClick={downloadImage}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <FontAwesomeIcon icon={faDownload} className="ml-2" />
                      {language === "ar" ? "تحميل الصورة" : "Télécharger"}
                    </button>

                    {/* Image Counter (for multiple images) */}
                    {selectedPhoto.fichiers.length > 1 && (
                      <div className="bg-gray-100 px-4 py-2 rounded-lg">
                        <span className="text-gray-700 font-medium">
                          {currentImageIndex + 1} /{" "}
                          {selectedPhoto.fichiers.length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Image Description (if multiple images have different descriptions) */}
                {selectedPhoto.fichiers.length > 1 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">
                      {language === "ar"
                        ? `الصورة ${currentImageIndex + 1} من ${
                            selectedPhoto.fichiers.length
                          }`
                        : `Image ${currentImageIndex + 1} sur ${
                            selectedPhoto.fichiers.length
                          }`}
                    </p>
                  </div>
                )}

                {/* Navigation Dots */}
                {selectedPhoto.fichiers.length > 1 && (
                  <div className="flex justify-center space-x-2 mt-6">
                    {selectedPhoto.fichiers.map((_, index) => (
                      <button
                        key={index}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentImageIndex
                            ? "bg-green-600"
                            : "bg-gray-300 hover:bg-gray-400"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToImage(index);
                        }}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotosPage;
